'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, Globe } from 'lucide-react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function ContactUsPage() {
  const { phoneNumber } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSubmitted(false);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to send inquiry.');
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            24/7 DEDICATED DESK
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Contact{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Faremaps
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            We are standing beside you to provide credible, real-time resolution to flight bookings
            and travel arrangements, always and ever.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Contact Details & Info (Left) */}
          <div className="lg:col-span-5 text-left space-y-6 animate-fadeInUp">
            {/* Quick Contact Cards */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-8">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2.5">
                <MessageSquare className="w-5 h-5 text-accent" />
                Reach Our Agents
              </h2>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Call Desk Phone
                  </h3>
                  <a
                    href={`tel:${phoneNumber}`}
                    className="text-base font-black text-slate-800 hover:text-accent transition-colors block mt-1"
                  >
                    {phoneNumber}
                  </a>
                  <span className="text-[10px] text-slate-400 font-bold block mt-1">
                    Toll-Free • Available 24 Hours / 7 Days
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Email Support
                  </h3>
                  <a
                    href="mailto:support@faremaps.com"
                    className="text-sm font-black text-slate-800 hover:text-accent transition-colors block mt-1"
                  >
                    support@faremaps.com
                  </a>
                  <span className="text-[10px] text-slate-400 font-bold block mt-1">
                    General Inquiries, Refund & Cancellation requests
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Global Headquarters
                  </h3>
                  <p className="text-xs font-bold text-slate-800 mt-1 leading-relaxed">
                    13900 NE 12th St Bellevue, WA 98005, USA
                  </p>
                </div>
              </div>
            </div>

            {/* Operating Hours card */}
            <div className="bg-slate-900 border border-white/5 rounded-3xl p-6 text-white text-left space-y-4">
              <h3 className="text-[10.5px] font-black text-accent uppercase tracking-widest flex items-center gap-2">
                <Clock className="w-4 h-4" /> Supporting Your Travels Globally
              </h3>
              <p className="text-slate-300 text-xs font-semibold leading-relaxed">
                Our support agents are fully distributed across several time zones to make sure your
                flight adjustments, rebookings, and inquiries are processed in real-time.
              </p>
              <div className="pt-2 border-t border-white/10 flex justify-between items-center text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                <span>Business Desk: 8AM - 11PM (EST)</span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5 text-accent" /> 24/7 Call Center
                </span>
              </div>
            </div>
          </div>

          {/* Interactive Form Card (Right) */}
          <div
            className="lg:col-span-7 text-left animate-fadeInUp"
            style={{ animationDelay: '100ms' }}
          >
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider mb-2">
                Submit An Inquiry
              </h2>
              <p className="text-slate-500 text-xs font-semibold mb-6">
                Fill out the form below and one of our flight coordinators will reach out to you
                within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="name"
                      className="text-[10.5px] font-black text-slate-500 uppercase tracking-wider"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="h-10 px-4 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-accent/40 focus:bg-white transition-all text-slate-800"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="email"
                      className="text-[10.5px] font-black text-slate-500 uppercase tracking-wider"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jane@example.com"
                      className="h-10 px-4 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-accent/40 focus:bg-white transition-all text-slate-800"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="phone"
                      className="text-[10.5px] font-black text-slate-500 uppercase tracking-wider"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      className="h-10 px-4 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-accent/40 focus:bg-white transition-all text-slate-800"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="subject"
                      className="text-[10.5px] font-black text-slate-500 uppercase tracking-wider"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Flight booking changes / Inquiry"
                      className="h-10 px-4 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-accent/40 focus:bg-white transition-all text-slate-800"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="message"
                    className="text-[10.5px] font-black text-slate-500 uppercase tracking-wider"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Enter the details of your flight or booking reference..."
                    rows={5}
                    className="p-4 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-accent/40 focus:bg-white transition-all text-slate-800 resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent hover:bg-accent-dark text-white font-black text-xs uppercase tracking-widest h-12 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-premium active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" /> {loading ? 'Sending Request...' : 'Send Message'}
                </button>

                {error && (
                  <p className="text-rose-600 text-xs font-extrabold mt-3 animate-fadeInUp text-center">
                    ✗ {error}
                  </p>
                )}

                {submitted && (
                  <p className="text-emerald-600 text-xs font-extrabold mt-3 animate-fadeInUp text-center">
                    ✓ Thank you! Your request has been sent to our expert flight desk successfully.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
